/**
 * `SessionExpiredDialog` - modal that fires when silent token refresh
 * fails mid-session.
 *
 * Per 04a primitives table:
 *   - `role="dialog"`, `aria-modal="true"`.
 *   - Focus trap (initial focus on the primary CTA; tabbing wraps).
 *   - Esc is intentionally disabled - the user must act.
 *   - Single primary action: "Sign in again", which calls
 *     `useAuth().signIn()` (loginRedirect) preserving the current
 *     pathname as `returnTo`.
 *
 * This component mounts at `AppShell` level (wired by `pages/accounts.tsx`
 * for now; promoted to a global mount in PR-E2 alongside the audit-emit
 * dialog wiring).
 */

import { useEffect, useRef } from 'react'
import type { KeyboardEvent } from 'react'
import { useLocation } from 'react-router-dom'

import { useAuth } from '../auth/use-auth'
import { Button } from './button'

export interface SessionExpiredDialogProps {
  readonly open: boolean
  /** Called when the user dismisses the dialog without re-authenticating
   *  (currently unused - Esc is suppressed - but supplied for tests). */
  readonly onDismiss?: () => void
}

export function SessionExpiredDialog({
  open,
  onDismiss,
}: SessionExpiredDialogProps): JSX.Element | null {
  const { signIn } = useAuth()
  const location = useLocation()
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (open) {
      // Defer to next tick so the dialog has mounted before we move focus.
      const id = window.setTimeout(() => buttonRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }
    return undefined
  }, [open])

  if (!open) return null

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    // Esc is suppressed by design; the user must re-authenticate.
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
    }
    // Trap focus within the dialog.
    if (event.key === 'Tab') {
      event.preventDefault()
      buttonRef.current?.focus()
    }
  }

  const handleSignIn = async (): Promise<void> => {
    const returnTo = `${location.pathname}${location.search}`
    await signIn(returnTo)
    onDismiss?.()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-body"
      onKeyDown={handleKeyDown}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="w-full max-w-md rounded-md bg-bg-surface p-murmuration-5 shadow-elev-3">
        <h2 id="session-expired-title" className="text-murmuration-h2 text-text-primary">
          Session expired
        </h2>
        <p
          id="session-expired-body"
          className="mt-murmuration-3 text-murmuration-body text-text-secondary"
        >
          Your sign-in expired and could not be refreshed automatically. Sign in again to continue.
        </p>
        <div className="mt-murmuration-5 flex justify-end">
          <Button
            ref={buttonRef}
            variant="primary"
            onClick={() => {
              void handleSignIn()
            }}
          >
            Sign in again
          </Button>
        </div>
      </div>
    </div>
  )
}
